import {
    formatLkqPricingDimensionLabel,
    LKQ_PRICING_ALL_LABEL,
    LKQ_PRICING_WILDCARD,
    sortLkqPricingStates,
} from './lkqPricingConstants';

/**
 * The five pricing tables (used/aftermarket and sublet markups, the same two
 * discounts, and the discount flag expectations) store the same shape: one row
 * per insurance company, vendor and state, with the percentage that combination
 * agreed to. Only the percentage field's name differs, so the rule model lives
 * here once and each table says how to read and write its own percentage.
 */
export interface LkqPricingEntry {
    insuranceCompanyId: string;
    /** Older rows can be missing one, which reads as "any vendor". */
    supplierId?: string;
    state: string;
    /** Missing means used and aftermarket. */
    partKind?: string;
    /** Vehicle make. Missing means any make. */
    make?: string;
    insuranceCompanyName?: string;
    supplierName?: string;
    /** Carrier group the insurer belongs to; its sub companies share it. */
    insuranceGroupId?: string;
    /**
     * Insurer id of the company whose row this one follows. Set on a sub company
     * added on its parent's rule: it has no row of its own in the table, and
     * every change to the parent is copied down to it.
     */
    parentInsuranceCompanyId?: string;
    /**
     * The group's parent, kept even after branching so a sub company with its
     * own row can be labelled "Allstate -> SafeAuto".
     */
    parentInsuranceCompanyName?: string;
}

/** An insurance company on a rule, by id and display name. */
export interface LkqPricingCompany {
    id: string;
    name: string;
}

/**
 * One rule as a pricing table shows it: an insurance company, the vendor and
 * states it applies to, and the percentage those add up to.
 *
 * A rule is a set of stored entries, not a single row. Entries are stored one
 * per (company, vendor, state) — which is what the invoicing pipeline looks a
 * line item up by — so a rule covering three states is three entries, and this
 * collapses them back into the thing the user created.
 *
 * Companies in the same group that agreed the same percentage share a rule the
 * way DRP carriers share a rule set: the primary owns it and its sub companies
 * point at it through `parentInsuranceCompanyId`, so they have no row of their
 * own. Branching a sub company clears that pointer and leaves the parent's name
 * behind, so its row reads "Allstate -> SafeAuto".
 */
export interface LkqPricingRule {
    /** `companyId|supplierId|percentage|partKind|make`, which is also its URL segment. */
    ruleKey: string;
    insuranceCompanyId: string;
    insuranceCompanyName: string;
    /** Sub companies on this rule; they have no row of their own. */
    includedCompanies: LkqPricingCompany[];
    /** Carrier group the primary belongs to, for looking up its group mates. */
    insuranceGroupId?: string;
    /** The group parent, kept after branching so the row reads under it. */
    parentInsuranceCompanyName?: string;
    supplierId: string;
    supplierName: string;
    partKind?: string;
    make?: string;
    /** Sorted state codes, or `['*']` for every state. */
    states: string[];
    /** Percentage as a 0..1 decimal, as stored. */
    percentage: number;
}

/** What a rule is set to, and what an inline cell edit restates for it. */
export interface LkqPricingRowValues {
    supplierId: string;
    supplierName: string;
    partKind?: string;
    make?: string;
    states: string[];
    percentage: number;
}

/** One company's identity on a rule, as the stored entries carry it. */
export interface LkqPricingRowCompany extends LkqPricingCompany {
    insuranceGroupId?: string;
    /** Set on a sub company riding along on the primary's row. */
    parentInsuranceCompanyId?: string;
    /** The group parent's name, kept on a branched sub company too. */
    parentInsuranceCompanyName?: string;
}

/**
 * How one table writes a stored entry. Each pricing table supplies this, which
 * is the only place its own percentage field is named.
 */
export type LkqPricingEntryFactory<T extends LkqPricingEntry> = (args: {
    company: LkqPricingRowCompany;
    state: string;
    values: LkqPricingRowValues;
}) => T;

export function lkqPricingRuleKey(
    insuranceCompanyId: string,
    supplierId: string,
    percentage: number,
    partKind?: string,
    make?: string,
): string {
    return `${insuranceCompanyId}|${supplierId}|${percentage}|${partKind ?? ''}|${make ?? ''}`;
}

/** The supplier id as stored, with a missing one read as "any vendor". */
export const lkqPricingSupplierIdOf = (entry: LkqPricingEntry): string =>
    entry.supplierId ?? LKQ_PRICING_WILDCARD;

/**
 * Whether two vendor selections can land on the same invoice line. "All
 * vendors" covers every one of them, so overlap is about coverage rather than
 * equality — which is what makes a second row ambiguous.
 */
export function lkqPricingSupplierOverlaps(a: string, b: string): boolean {
    return a === LKQ_PRICING_WILDCARD || b === LKQ_PRICING_WILDCARD || a === b;
}

/** The same question for states: "All" covers every one of them. */
export function lkqPricingStateOverlaps(states: string[], state: string): boolean {
    return (
        states.includes(LKQ_PRICING_WILDCARD) ||
        state === LKQ_PRICING_WILDCARD ||
        states.includes(state)
    );
}

const PRICING_LINE_KINDS = ['used', 'aftermarket', 'oem', 'glass', 'reman'] as const;

/** Whether a stored part kind would price this line kind. Blank is legacy used and aftermarket. */
function pricingPartKindCovers(rowKind: string | undefined, lineKind: string): boolean {
    const row = (rowKind ?? '').trim().toLowerCase();
    if (row === LKQ_PRICING_WILDCARD) return true;
    if (!row) return lineKind === 'used' || lineKind === 'aftermarket';
    return row === lineKind;
}

/**
 * Whether two part kinds can price the same line. A blank kind is used and
 * aftermarket; `*` is every kind.
 */
export function lkqPricingPartKindOverlaps(a?: string, b?: string): boolean {
    return PRICING_LINE_KINDS.some(
        (kind) => pricingPartKindCovers(a, kind) && pricingPartKindCovers(b, kind),
    );
}

/**
 * Whether two make rows would be the same rule.
 * A blank or `*` row matches every vehicle, and a named row wins over it for
 * that make, so those two are allowed to coexist. Two blank rows, or the same
 * named make twice, are the same rule.
 */
export function lkqPricingMakeOverlaps(a?: string, b?: string): boolean {
    const left = (a ?? '').trim().toLowerCase();
    const right = (b ?? '').trim().toLowerCase();
    const leftAny = !left || left === LKQ_PRICING_WILDCARD;
    const rightAny = !right || right === LKQ_PRICING_WILDCARD;
    if (leftAny && rightAny) return true;
    if (leftAny || rightAny) return false;
    return left === right;
}

/** How many sub company names fit beside a primary before the rest are counted. */
const SUB_COMPANY_NAME_LIMIT = 2;

/** "Includes SafeAuto, Esurance +2 more" — what a rule also covers. */
export function formatLkqPricingSubCompanySummary(rule: LkqPricingRule): string {
    const names = rule.includedCompanies.map((company) => company.name);
    const shown = names.slice(0, SUB_COMPANY_NAME_LIMIT).join(', ');
    const rest = names.length - SUB_COMPANY_NAME_LIMIT;
    return `Includes ${shown}${rest > 0 ? ` +${rest} more` : ''}`;
}

/** Label for the companies a rule covers: "All", the name, or "Name +2 more". */
export function formatLkqPricingCompaniesLabel(rule: LkqPricingRule): string {
    if (rule.insuranceCompanyId === LKQ_PRICING_WILDCARD) return LKQ_PRICING_ALL_LABEL;
    if (rule.includedCompanies.length === 0) return rule.insuranceCompanyName;
    return `${rule.insuranceCompanyName} +${rule.includedCompanies.length} more`;
}

/** Every company a rule covers, primary first — what it writes entries for. */
export function lkqPricingRuleCompanies(rule: LkqPricingRule): LkqPricingCompany[] {
    return [
        { id: rule.insuranceCompanyId, name: rule.insuranceCompanyName },
        ...rule.includedCompanies,
    ];
}

/** Names a rule covers, for search. */
export function lkqPricingRuleNames(rule: LkqPricingRule): string[] {
    return [
        rule.insuranceCompanyName,
        rule.parentInsuranceCompanyName ?? '',
        ...rule.includedCompanies.map((company) => company.name),
    ];
}

interface RuleAccumulator {
    ruleKey: string;
    insuranceCompanyId: string;
    insuranceCompanyName: string;
    insuranceGroupId?: string;
    parentInsuranceCompanyId?: string;
    parentInsuranceCompanyName?: string;
    supplierId: string;
    supplierName: string;
    partKind?: string;
    make?: string;
    states: string[];
    percentage: number;
}

/**
 * Collapse the stored entries into one accumulator per company, vendor and
 * percentage, keeping every state that combination was saved for.
 */
function accumulateByCompany<T extends LkqPricingEntry>(
    entries: T[],
    percentageOf: (entry: T) => number,
): Map<string, RuleAccumulator> {
    const byKey = new Map<string, RuleAccumulator>();

    for (const entry of entries) {
        const supplierId = lkqPricingSupplierIdOf(entry);
        const percentage = percentageOf(entry);
        const ruleKey = lkqPricingRuleKey(entry.insuranceCompanyId, supplierId, percentage, entry.partKind, entry.make);
        const existing = byKey.get(ruleKey);
        if (existing) {
            existing.states.push(entry.state);
            // Names and group fields ride along on every entry, so the first
            // one that carries a value answers for the whole combination.
            existing.insuranceGroupId ??= entry.insuranceGroupId;
            existing.parentInsuranceCompanyId ??= entry.parentInsuranceCompanyId;
            existing.parentInsuranceCompanyName ??= entry.parentInsuranceCompanyName;
            continue;
        }
        byKey.set(ruleKey, {
            ruleKey,
            insuranceCompanyId: entry.insuranceCompanyId,
            insuranceCompanyName: formatLkqPricingDimensionLabel(
                entry.insuranceCompanyId,
                entry.insuranceCompanyName,
                entry.insuranceCompanyId,
            ),
            insuranceGroupId: entry.insuranceGroupId,
            parentInsuranceCompanyId: entry.parentInsuranceCompanyId,
            parentInsuranceCompanyName: entry.parentInsuranceCompanyName,
            supplierId,
            supplierName: formatLkqPricingDimensionLabel(
                supplierId,
                entry.supplierName,
                supplierId,
            ),
            partKind: entry.partKind,
            make: entry.make,
            states: [entry.state],
            percentage,
        });
    }

    return byKey;
}

/**
 * One rule per row of a pricing table. A sub company pointing at a primary with
 * the same vendor and percentage is folded under it rather than getting a row;
 * one whose primary is no longer configured stands on its own, so a stale
 * pointer can never hide a row that is still in force.
 */
export function groupLkqPricingRules<T extends LkqPricingEntry>(
    entries: T[],
    percentageOf: (entry: T) => number,
): LkqPricingRule[] {
    const byKey = accumulateByCompany(entries, percentageOf);
    const rules = new Map<string, LkqPricingRule>();
    const followers: RuleAccumulator[] = [];

    const toRule = (entry: RuleAccumulator): LkqPricingRule => ({
        ruleKey: entry.ruleKey,
        insuranceCompanyId: entry.insuranceCompanyId,
        insuranceCompanyName: entry.insuranceCompanyName,
        includedCompanies: [],
        insuranceGroupId: entry.insuranceGroupId,
        parentInsuranceCompanyName: entry.parentInsuranceCompanyName,
        supplierId: entry.supplierId,
        supplierName: entry.supplierName,
        partKind: entry.partKind,
        make: entry.make,
        states: sortLkqPricingStates(entry.states),
        percentage: entry.percentage,
    });

    /** The primary an accumulator follows, when it is on that primary's rule. */
    const parentKeyOf = (entry: RuleAccumulator): string | undefined =>
        entry.parentInsuranceCompanyId
            ? lkqPricingRuleKey(
                  entry.parentInsuranceCompanyId,
                  entry.supplierId,
                  entry.percentage,
                  entry.partKind,
                  entry.make,
              )
            : undefined;

    for (const entry of Array.from(byKey.values())) {
        const parentKey = parentKeyOf(entry);
        if (parentKey && byKey.has(parentKey)) {
            followers.push(entry);
            continue;
        }
        rules.set(entry.ruleKey, toRule(entry));
    }

    for (const entry of followers) {
        const rule = rules.get(parentKeyOf(entry) as string);
        // The primary exists but follows a third entry; give this one a row of
        // its own rather than letting a chain swallow it.
        if (!rule) {
            rules.set(entry.ruleKey, toRule(entry));
            continue;
        }
        rule.includedCompanies.push({
            id: entry.insuranceCompanyId,
            name: entry.insuranceCompanyName,
        });
    }

    return Array.from(rules.values())
        .map((rule) => ({
            ...rule,
            includedCompanies: [...rule.includedCompanies].sort((a, b) =>
                a.name.localeCompare(b.name),
            ),
        }))
        .sort((a, b) => {
            const companyCompare = a.insuranceCompanyName.localeCompare(b.insuranceCompanyName);
            if (companyCompare !== 0) return companyCompare;
            const vendorCompare = a.supplierName.localeCompare(b.supplierName);
            if (vendorCompare !== 0) return vendorCompare;
            return a.percentage - b.percentage;
        });
}

export function findLkqPricingRule(
    rules: LkqPricingRule[],
    ruleKey: string,
): LkqPricingRule | undefined {
    return rules.find((rule) => rule.ruleKey === ruleKey);
}

function entryPricingRuleKey<T extends LkqPricingEntry>(
    entry: T,
    insuranceCompanyId: string,
    percentageOf: (entry: T) => number,
): string {
    return lkqPricingRuleKey(
        insuranceCompanyId,
        lkqPricingSupplierIdOf(entry),
        percentageOf(entry),
        entry.partKind,
        entry.make,
    );
}

/**
 * Drop a rule: the primary's entries and the sub companies that follow it,
 * matched on insurer, vendor, percentage, part kind, and make.
 */
export function removeLkqPricingRule<T extends LkqPricingEntry>(
    entries: T[],
    ruleKey: string,
    percentageOf: (entry: T) => number,
): T[] {
    return entries.filter((entry) => {
        if (entryPricingRuleKey(entry, entry.insuranceCompanyId, percentageOf) === ruleKey) return false;
        if (!entry.parentInsuranceCompanyId) return true;
        return entryPricingRuleKey(entry, entry.parentInsuranceCompanyId, percentageOf) !== ruleKey;
    });
}

/**
 * Drop the entries these companies hold that the given values would overlap, so
 * writing a rule replaces what it covers instead of stacking a second,
 * ambiguous row on top of it. Rules for other vendors, states, part kinds, or
 * makes are left alone.
 *
 * Companies are matched by id, so an "All insurance companies" rule and a rule
 * for one named company coexist: the named one is how a shop overrides the
 * blanket percentage, which is the way the pricing tables read.
 */
export function dropOverlappingLkqPricingEntries<T extends LkqPricingEntry>(
    entries: T[],
    companyIds: Set<string>,
    values: LkqPricingRowValues,
): T[] {
    return entries.filter(
        (entry) =>
            !(
                companyIds.has(entry.insuranceCompanyId) &&
                lkqPricingSupplierOverlaps(lkqPricingSupplierIdOf(entry), values.supplierId) &&
                lkqPricingStateOverlaps(values.states, entry.state) &&
                lkqPricingPartKindOverlaps(entry.partKind, values.partKind) &&
                lkqPricingMakeOverlaps(entry.make, values.make)
            ),
    );
}

/**
 * Companies that already hold a row the given values would overlap. The
 * invoicing pipeline reads one row per company, vendor, state, part kind, and
 * make, so a second one that can price the same line is ambiguous.
 */
export function conflictingLkqPricingCompanies<T extends LkqPricingEntry>(
    entries: T[],
    companies: LkqPricingCompany[],
    values: LkqPricingRowValues,
): string[] {
    const clashes = new Set<string>();
    for (const company of companies) {
        const clash = entries.some(
            (entry) =>
                entry.insuranceCompanyId === company.id &&
                lkqPricingSupplierOverlaps(lkqPricingSupplierIdOf(entry), values.supplierId) &&
                lkqPricingStateOverlaps(values.states, entry.state) &&
                lkqPricingPartKindOverlaps(entry.partKind, values.partKind) &&
                lkqPricingMakeOverlaps(entry.make, values.make),
        );
        if (clash) clashes.add(company.name);
    }
    return Array.from(clashes);
}

/** The stored entries for one company under a rule: one per state. */
export function toLkqPricingEntries<T extends LkqPricingEntry>(
    company: LkqPricingRowCompany,
    values: LkqPricingRowValues,
    buildEntry: LkqPricingEntryFactory<T>,
): T[] {
    return values.states.map((state) => buildEntry({ company, state, values }));
}

/**
 * Restate a whole rule: the primary and every sub company on it are rewritten
 * from `values`, so an inline cell edit keeps the set identical by construction
 * rather than asking the user to repeat the change on each company.
 *
 * Retuning a rule onto a vendor or a state one of its companies is already
 * covered for would leave two rows for the same line item, so what the new
 * values cover is replaced rather than added to.
 */
export function applyLkqPricingRuleChange<T extends LkqPricingEntry>(
    entries: T[],
    rule: LkqPricingRule,
    values: LkqPricingRowValues,
    percentageOf: (entry: T) => number,
    buildEntry: LkqPricingEntryFactory<T>,
): T[] {
    const companyIds = new Set(lkqPricingRuleCompanies(rule).map((company) => company.id));
    const kept = dropOverlappingLkqPricingEntries(
        removeLkqPricingRule(entries, rule.ruleKey, percentageOf),
        companyIds,
        values,
    );

    const rows = [
        ...toLkqPricingEntries(
            {
                id: rule.insuranceCompanyId,
                name: rule.insuranceCompanyName,
                insuranceGroupId: rule.insuranceGroupId,
                parentInsuranceCompanyName: rule.parentInsuranceCompanyName,
            },
            values,
            buildEntry,
        ),
        ...rule.includedCompanies.flatMap((company) =>
            toLkqPricingEntries(
                {
                    ...company,
                    insuranceGroupId: rule.insuranceGroupId,
                    parentInsuranceCompanyId: rule.insuranceCompanyId,
                    parentInsuranceCompanyName: rule.insuranceCompanyName,
                },
                values,
                buildEntry,
            ),
        ),
    ];

    return [...kept, ...rows];
}
